import { useQuery } from '@tanstack/react-query'
import { CircleAlert, CircleCheck, CircleOff } from 'lucide-react'
import type { ReactNode } from 'react'

import { Badge, Tooltip, type StatusTone } from '@aeris/ui'

import { fetchHealth } from '../lib/api.ts'

/**
 * Live API status.
 *
 * Three states, not two. `degraded` is the one that earns its place: the API
 * is answering but a dependency is not, which is operationally different from
 * "nothing reachable" and is the first visible proof that liveness and
 * readiness are modelled separately (ADR-0007).
 *
 * Each state carries text, an icon and a tone — never colour alone
 * (docs/UI_UX.md).
 */

type StatusView = {
  label: string
  detail: string
  tone: StatusTone
  icon: ReactNode
}

const VIEWS = {
  online: {
    label: 'Online',
    detail: 'API e banco de dados respondendo normalmente.',
    tone: 'success',
    icon: <CircleCheck aria-hidden="true" className="h-3 w-3" />,
  },
  degraded: {
    label: 'Degradado',
    detail:
      'A API está no ar, mas uma dependência não respondeu. Operações que dependem do banco podem falhar.',
    tone: 'warning',
    icon: <CircleAlert aria-hidden="true" className="h-3 w-3" />,
  },
  offline: {
    label: 'Offline',
    /*
     * This used to promise that "o Aeris continua utilizável" and that changes
     * would be sent when the connection returned. Neither was true: there is
     * no queue, and nothing can be saved. An indicator that overstates what the
     * product does is worse than no indicator, because an operator believes it.
     */
    detail:
      'Não foi possível falar com a API. As telas abrem com o que está guardado neste computador, mas nada pode ser salvo até a conexão voltar.',
    tone: 'danger',
    icon: <CircleOff aria-hidden="true" className="h-3 w-3" />,
  },
} as const satisfies Record<string, StatusView>

export const ApiStatus = (): ReactNode => {
  const { data, isError, isPending } = useQuery({
    queryKey: ['health'],
    queryFn: ({ signal }) => fetchHealth(signal),
    // Calm cadence. Aggressive polling is prohibited (Master Spec section 27)
    // and this runs on branch machines all day.
    refetchInterval: 30_000,
    // Pausing while the tab is hidden keeps a background window from burning
    // cycles on hardware that has none to spare.
    refetchIntervalInBackground: false,
    retry: 1,
    staleTime: 10_000,
  })

  const view: StatusView = isError
    ? VIEWS.offline
    : data?.status === 'degraded'
      ? VIEWS.degraded
      : VIEWS.online

  // The first load shows nothing rather than a misleading "Offline".
  if (isPending) {
    return (
      <span className="text-(length:--text-xs) text-(--text-muted)">
        Verificando…
      </span>
    )
  }

  return (
    <Tooltip content={view.detail} side="bottom">
      <span>
        <Badge tone={view.tone} icon={view.icon}>
          <span className="sr-only">Status da API: </span>
          {view.label}
        </Badge>
      </span>
    </Tooltip>
  )
}
